import React, { useEffect, useState, useRef } from 'react';

import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import ReactRouterPropTypes from 'react-router-prop-types';
import { connect, useSelector } from 'react-redux';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import ReactImageMagnify from 'react-image-magnify';
import PVPropsProductDetailType from './PropTypes/PVPropsProductDetailType';
import { getItemWithApiFallback } from '../../redux/products/items/actionsAsync';
import { selectItemId } from '../../redux/products/items/selectors';

import {
    createHTMLText,
    getPriceString,
    formatPhoneNumber,
    phoneNumberFormatTypes,
} from '../../helpers/textFunctions';
import PVFreeShipping from './PVFreeShipping';
import PVShopWrapper from './PVShopWrapper';
import PVSuggestionsSlider from './ProductSliders/PVSuggestionsSlider';
import { featuredTypes } from '../../redux/products/featured';
import PVCartQuantitySpinner from '../Shared/Content/PVCartQuantitySpinner';
import getFallbackImage from '../../helpers/getFallbackImage';
import { selectActiveDealerKey, selectUserIsRetail } from '../../redux/userProfile/selectors';

const PVProductDetail = ({ product, apiGetItemDetail, dealerId }) => {
    const isMounted = useRef(null);
    const params = useParams();
    const history = useHistory();
    const categoryPath = '/Shop/Category';
    const productPath = '/Shop/Product';
    const shoppingTagline = 'Shop at PerfectVision today for design and performance at a competitive price. Shop online for 1-2 day delivery.';

    const [productLoaded, setProductLoaded] = useState(false);
    const [showApproved, setShowApproved] = useState({});
    const [heading, setHeading] = useState('');
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [pricingDetails, setPricingDetails] = useState([]);
    const [showPage, setShowPage] = useState(false);
    const [showLoading, setShowLoading] = useState(true);
    const [isRetail] = useState(useSelector(selectUserIsRetail));
    const [metaDescription, setMetaDescription] = useState('');

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleApiResults = (apiResults) => {
        // if (productLoaded && !product.Dimensions) {
        //     apiGetItemDetail(params.itemID);
        // }
        if (!apiResults) {
            history.replace('/Error/500');
        }
        if (
            apiResults &&
            (apiResults.ID === 0 ||
                typeof apiResults.ID === 'undefined' ||
                apiResults.ItemMeetsDisplayRequirements === false)
        ) {
            history.replace('/Error/404');
        }
        if (apiResults && apiResults.ImagePath && isMounted.current) {
            setProductLoaded(true);
        }
        if (apiResults && apiResults.Breadcrumbs && isMounted.current) {
            const bc = [...apiResults.Breadcrumbs];
            bc.sort((a, b) => a.sortOrder - b.sortOrder);
            setBreadcrumbs(bc);
        }
        if (apiResults && apiResults.Logos && isMounted.current) {
            setShowApproved({
                ATT: apiResults.Logos.DTV,
                Viasat: apiResults.Logos.Exede,
                FreeShipping: apiResults.Logos.FreeShipping,
            });
        }
        if (apiResults && apiResults.PricingDetails && isMounted.current) {
            const pd = [...apiResults.PricingDetails];
            pd.sort((a, b) => a.QtyMin - b.QtyMin);
            setPricingDetails(pd);             
        }

        if (apiResults && isMounted.current) {
            let shortDesc = '';

            if (apiResults.ShortDesc && apiResults.ShortDesc.length > 0)
            {
                const firstPeriod = apiResults.ShortDesc.indexOf('.');

                if (firstPeriod > 0)
                {
                    shortDesc = apiResults.ShortDesc.substring(0, firstPeriod + 1);
                }
                else
                {
                    shortDesc = apiResults.ShortDesc;
                }
            }

            let meta = '';

            if (shortDesc.length > 0 && shortDesc !== apiResults.Name)
            {
                meta = `${apiResults.Name} - ${apiResults.ItemID.toUpperCase()} - ${shortDesc} - ${shoppingTagline}`;
            }
            else
            {
                meta = `${apiResults.Name} - ${apiResults.ItemID.toUpperCase()} - ${shoppingTagline}`;
            }

            setMetaDescription(meta);
        }

        setShowLoading(false);
        setShowPage(true);       
    };

    useEffect(() => {
        apiGetItemDetail(params.itemID)
            .then((results) => {
                handleApiResults(results);
            })
            .catch((results) => {
                if (results.ProductID === 0) history.push('/Error/500');
                else {
                    history.push('/Error/404');
                }
            });
    }, [params, dealerId]);

    useEffect(() => {
        if (breadcrumbs && breadcrumbs.length > 0) {
            const h =
                (breadcrumbs.filter((x) => x.linkText !== 'Home') || []).sort(
                    (x) => x.sortOrder
                )[0] || {};
            if (isMounted.current) {
                setHeading(h.linkText || '');
            }
        }
    }, [breadcrumbs]);

    // useEffect(() => {
    //     if (productLoaded && !pDim) {
    //         apiGetItemDetail(params.itemID)
    //             .then(results => handleApiResults(results))
    //             .catch(() => history.push('/Error/500'));
    //     }
    // }, [pDim, productLoaded]);
    const ProductInformation = () => {
        if (productLoaded) {
            let prodInfo = product.ShortDesc || '';
            if (prodInfo.length === 0 && product.ProductTexts) {
                const prodDetails = product.ProductTexts.find(
                    (text) => text.TypeName === 'Product Details'
                );
                if (prodDetails) {
                    prodInfo = prodDetails.TextBody;
                }
            } else if (prodInfo.indexOf('Product-specific')) {
                prodInfo = prodInfo.substring(0, prodInfo.indexOf('Product-specific'));
            }
            // truncate prodInfo if longer than 200 characters
            if (prodInfo.length > 200) {
                prodInfo = prodInfo.substring(0, 199).concat('...');
            }
            return (
                <React.Fragment>
                    {/* eslint-disable-next-line react/no-danger  */}
                    <div dangerouslySetInnerHTML={createHTMLText(prodInfo)} />
                </React.Fragment>
            );
        }
        return <React.Fragment>...</React.Fragment>;
    };

    const ProductReplacement = () => {
        if (productLoaded && product.ReplacementItem) {
            const path = `${productPath}/${product.ReplacementItem}`;
            return (
                <div className="product-replacement">
                    <Link to={path} alt={product.ReplacementItem}>
                        Replacement Item: {product.ReplacementItem}
                    </Link>
                </div>
            );
        }
        return null;
    };

    const ProductAlternative = () => {
        if (productLoaded && product.AlternateItem) {
            const path = `${productPath}/${product.AlternateItem}`;
            return (
                <div className="product-alternative">
                    <Link to={path} alt={product.AlternateItem}>
                        Alternate Item: {product.AlternateItem}
                    </Link>
                </div>
            );
        }
        return null;
    };

    const pricing = pricingDetails.map((pd) => (
        <tr key={pd.QtyMin}>
            <th scope="row">
                {pd.QtyMin}
                {pd.QtyMax === 999999999 ? '+' : '-'}
                {pd.QtyMax === 999999999 ? '' : pd.QtyMax}
            </th>
            <td>
                <span className="price">
                    <ins>{getPriceString(pd.Price)}</ins>
                </span>
            </td>
        </tr>
    ));



    

    const buildDimensionText = (specs) => {
        let specText = "These are the product's dimensions:<hr/>";
        // eslint-disable-next-line func-names
        specs.forEach(function (spec) {
            specText = `${specText}<p><strong>${spec.UnitID}</strong><br/>`;
            specText = `${specText}${spec.Width.toFixed(2)} in. wide x ${spec.Height.toFixed(
                2
            )} in. high x ${spec.Depth} in. deep<br/>`;
            const specVolume = spec.Width * spec.Height * spec.Depth;
            specText = `${specText}Volume: ${specVolume.toFixed(2)} cubic in.<br/>`;
            specText = `${specText}Weight: ${spec.Weight} lbs.<br/>`;
            specText = `${specText}Quantity: ${spec.Quantity} ea.</p>`;
        });
        return specText;
    };

    const buildBOMText = (bomItems) => {
        // return (
        //     <div className="product-alternative">
        //         <Link to={path} alt={product.AlternateItem}>
        //             Alternate Item: {product.AlternateItem}
        //         </Link>
        //     </div>
        // );

        let bomText = 'Items contained in kit:<br/><ul>';
        // eslint-disable-next-line func-names
        bomItems.forEach(function (bomItem) {
            if (bomItem.ItemMeetsDisplayRequirements === true) {
                const path = `${productPath}/${bomItem.ItemID}`;
                bomText = `${bomText}<li><a href='${path}' alt='${bomItem.ItemID}'>${bomItem.Name}</a></li>`;
            } else {
                bomText = `${bomText}<li>${bomItem.Name}</li>`;
            }
        });
        bomText = `${bomText}</ul>`;
        return bomText;
    };

    const InfoTabs = () => {
        if (productLoaded && product.ProductTexts) {
            const productTexts = [...product.ProductTexts];
            if (product.IsBOM === true) {
                productTexts.push({
                    ID: 1,
                    TextBody: buildBOMText(product.BOMProduct),
                    WebDisplay: true,
                    SortOrder: 99999,
                    TypeName: 'Kit Items',
                });
            }
            if (product.Dimensions.length > 0) {
                productTexts.push({
                    ID: 1,
                    TextBody: buildDimensionText(product.Dimensions),
                    WebDisplay: true,
                    SortOrder: 99999,
                    TypeName: 'Dimensions',
                });
            }

            if (productTexts.length > 0) {
                productTexts.sort((a, b) => a.SortOrder - b.SortOrder);
                var newProductTexts = groupBy(productTexts, 'TypeName');
                var newProductArray = Object.entries(newProductTexts);

                return (
                    <Tabs id="tab-details-product" className="tab-details-product pv-tabs">
                        {newProductArray.map((productText, key) => {
                            const id = key + 2;
                            if (productText[1][0].WebDisplay == true) {
                                return (
                                    /* eslint-disable react/no-danger  */
                                    <Tab
                                        key={productText[1][0].ID}
                                        eventKey={id}
                                        title={productText[0]}
                                    >
                                        {productText[1].map((textLinks, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    className="box-content"
                                                    dangerouslySetInnerHTML={createHTMLText(
                                                        textLinks.TextBody
                                                    )}
                                                />
                                            );
                                        })}
                                    </Tab>
                                    /* eslint-enable react/no-danger  */
                                );
                            }
                            return null;
                        })}
                    </Tabs>
                );
            }
        }
        return null;
    };

    return (
        <React.Fragment>
            {showPage && (
                <React.Fragment>
                    <PVShopWrapper
                        heading={heading}
                        menu={[]}
                        breadcrumbs={breadcrumbs}
                        categorypath={categoryPath}
                    >
                        <Helmet>
                            <title>{`${product.Name} - ${product.ItemID} | PerfectVision`}</title>
                            <meta name="description" content={`${metaDescription}`}/>
                            {/*Tell search engines that /Shop/Product/ (in Pascal case) is the "canonical" form for the */}
                            {/*URL of our product detail pages (the form they should index and show in search results). */}
                            <link rel="canonical" href={`https://www.perfect-vision.com/Shop/Product/${product.ItemID}`} />
                        </Helmet>
                        <div className={`detail-product ${productLoaded ? '' : 'hidden'}`}>
                            <div className="row row-no-gutters product-content-single">
                                <div className="col-md-4 col-sm-12">
                                    <div className="pv-product-detail-img">
                                        {productLoaded && (product.ImagePath || '').length > 0 && (
                                            <ReactImageMagnify
                                                enlargedImagePosition="over"
                                                {...{
                                                    smallImage: {
                                                        alt: product.ItemID,
                                                        src: product.ImagePath,
                                                        width: 272,
                                                        height: 272,
                                                        onError: (e) => getFallbackImage(e),
                                                    },
                                                    largeImage: {
                                                        alt: product.ItemID,
                                                        src: product.ImagePath,
                                                        width: 400,
                                                        height: 400,
                                                        onError: (e) => getFallbackImage(e),
                                                    },
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-5 col-sm-6">
                                    <div className="product-info-main">
                                        <div className="product-name">
                                            {productLoaded ? product.Name : 'loading...'}
                                        </div>
                                        <span className="sku">
                                            {productLoaded
                                                ? `Item ID: ${product.ItemID}`
                                                : 'loading...'}
                                        </span>
                                        <div className="product-information">
                                            <ProductInformation />
                                        </div>
                                        <ProductReplacement />
                                        <ProductAlternative />
                                        <div className="approved-img-group hidden-xs">
                                            {showApproved.ATT && (
                                                <img
                                                    src="/WebSupport/PV_React/Webstore/ATT-Approved.jpg"
                                                    alt="ATT Approved"
                                                />
                                            )}
                                            {showApproved.Viasat && (
                                                <img
                                                    src="/WebSupport/PV_React/Webstore/Viasat-Approved.jpg"
                                                    alt="Viasat Approved"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                    <div className="product-info-price">
                                        <div className="product-info-price-width-wrapper">
                                            {showApproved.FreeShipping && <PVFreeShipping />}
                                            <table className="table product-info-price-table">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Quantity</th>                                                       
                                                             {isRetail != false ?
                                                                (<th scope="col">List Price</th>)
                                                                :
                                                                (<th scope="col">Price</th>)
                                                            }                                                 
                                                        </tr>
                                                    
                                                </thead>
                                                <tbody>{pricing}</tbody>
                                            </table>
                                            <div
                                                className={`no-sale ${
                                                    product.ItemMeetsPurchaseRequirements ===
                                                        false &&
                                                    product.UsersAccountIsBlockedOrStopped
                                                        ? ''
                                                        : 'hidden'
                                                }`}
                                            >
                                                This account is not able to make purchases
                                            </div>
                                            <div
                                                className={`no-sale ${
                                                    product.ItemMeetsPurchaseRequirements ===
                                                        false &&
                                                    product.UsersAccountIsBlockedOrStopped ===
                                                        false &&
                                                    product.InternalOnly
                                                        ? ''
                                                        : 'hidden'
                                                }`}
                                            >
                                                Internal Only
                                            </div>
                                            <div
                                                className={`no-sale ${
                                                    product.ItemMeetsPurchaseRequirements ===
                                                        false &&
                                                    product.UsersAccountIsBlockedOrStopped ===
                                                        false &&
                                                    product.InternalOnly === false &&
                                                    product.SwitchAccountsToPurchase
                                                        ? ''
                                                        : 'hidden'
                                                }`}
                                            >
                                                Switch accounts to purchase
                                            </div>
                                            <div
                                                className={`no-sale ${
                                                    product.ItemMeetsPurchaseRequirements ===
                                                        false &&
                                                    product.UsersAccountIsBlockedOrStopped ===
                                                        false &&
                                                    product.InternalOnly === false &&
                                                    product.SwitchAccountsToPurchase === false
                                                        ? ''
                                                        : 'hidden'
                                                }`}
                                            >
                                                Call to check availability:
                                                <br />
                                                {formatPhoneNumber(
                                                    product.OutOfStockPhoneNumber,
                                                    phoneNumberFormatTypes.DASHES
                                                )}
                                            </div>
                                            <div
                                                className="product-info-add-to-cart"
                                            >
                                                {productLoaded && (
                                                    <div className="quantity">
                                                        {product.ItemMeetsPurchaseRequirements ? (
                                                            <h6 className="quantity-title">
                                                                Quantity:
                                                            </h6>
                                                        ) : (
                                                            <div>
                                                                <br/>
                                                                <br/>
                                                                <br/>
                                                            </div>
                                                        )}
                                                        <PVCartQuantitySpinner
                                                            itemId={product.ItemID}
                                                            initialValue={1}
                                                            showQuantity
                                                            useAddToCartBtn={product.ItemMeetsPurchaseRequirements}
                                                            useAddToFavoritesBtn
                                                            parentName="Product Detail"
                                                            itemMeetsPurchaseRequirements={product.ItemMeetsPurchaseRequirements}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="approved-img-group2 visible-xs">
                                                {showApproved.ATT && (
                                                    <img
                                                        src="/WebSupport/PV_React/Webstore/ATT-Approved.jpg"
                                                        alt="ATT Approved"
                                                    />
                                                )}
                                                {showApproved.Viasat && (
                                                    <img
                                                        src="/WebSupport/PV_React/Webstore/Viasat-Approved.jpg"
                                                        alt="Viasat Approved"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-12">
                                    {productLoaded && product.Dimensions ? (
                                        <InfoTabs />
                                    ) : (
                                        <div className="pv-spinner md" />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div
                            className={`detail-product ${
                                productLoaded && product.ID === 0 ? '' : 'hidden'
                            }`}
                        >
                            <h4>There was a problem loading that Item ID...</h4>
                        </div>
                    </PVShopWrapper>
                    {(dealerId > 0) ? (
                    <div className="container">
                        <PVSuggestionsSlider
                            featuredType={featuredTypes.SUGGESTED}
                            itemId={params.itemID}
                            numProducts={5}
                        />
                        <PVSuggestionsSlider
                            featuredType={featuredTypes.ALSOPURCHASED}
                            itemId={params.itemID}
                            prodDetail
                            numProducts={5}
                        />
                    </div> ) : null}
                </React.Fragment>
            )}
            {showLoading && <div className="pv-spinner lg" />}
        </React.Fragment>
    );
};

PVProductDetail.propTypes = {
    match: ReactRouterPropTypes.match.isRequired,
    history: ReactRouterPropTypes.history.isRequired,
    apiGetItemDetail: PropTypes.func,
    product: PVPropsProductDetailType,
    dealerId: PropTypes.string,
    
   
    
};
PVProductDetail.defaultProps = {
    product: {},
    apiGetItemDetail: () => {},
    dealerId: '',
   
    
};

function groupBy(data, key) {
    return data.reduce((acc, x) => {
        acc[x[key]] = [...(acc[x[key]] || []), x];
        return acc;
    }, {});
};

const mapStateToProps = (state, ownProps) => {
    const { params } = ownProps.match;
    return {
        product: selectItemId(state, params.itemID),
        dealerId: selectActiveDealerKey(state),
        
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        apiGetItemDetail: (itemId) => dispatch(getItemWithApiFallback(itemId)),
    
      
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PVProductDetail);
